import {
  IonButton,
  IonChip,
  IonContent,
  IonIcon,
  IonLabel,
  IonLoading,
  IonPage,
  IonSearchbar,
  useIonViewWillEnter,
} from '@ionic/react';
import { close, filter } from 'ionicons/icons';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { BleService } from 'src/ble/BleService';
import { MobileControllersGrid } from 'src/components/controllers/MobileControllersGrid';
import { DqText } from 'src/components/dq-text';
import MobileHeader from 'src/components/reusable-components/MobileHeader';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { GenericMessages } from 'src/intl/GenericMessages';
import { Controller } from 'src/models/controller';
import { Firmware, SearchFirmwareRequest } from 'src/models/firmware';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { BleConnectionState } from 'src/redux/ble/constants';
import { selectBleIsLocked } from 'src/redux/ble/selectors';
import { getOrgName } from 'src/utils/helpers/referenceHelpers';
import { useHasPermission } from 'src/utils/PermissionGuard';
import { useSocketList } from 'src/utils/socket';
import { SocketContextPortal } from 'src/utils/socket/context/portalSocketContext';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { useBle } from '../../ble/useBle';
import { appBarActions } from '../../redux/appBar/actions';
import { getFirmwareDisplayFromFirmware } from '../../utils/helpers/firmwareHelpers';
import './MobileControllersListPage.scss';
import FilterControllerModal from './FilterControllerModal';
import { UpdateFirmwareModal } from './UpdateFirmwareModal';
import { Roles } from '@dataquiver/stoneage-roles';
import { useLimitedMode } from '../../utils/auth/useLimitedMode';
import { ControllerListPageActions, ControllerListPageState } from '../controllers/ControllersListPage';
import { useSocketSearch } from '../../utils/socket/useSocketSearch';
import { ListOrganizationsRequest } from '../../models/organization';
import { escapeRegExp } from 'lodash';
import { useHasBluetoothLE } from 'src/utils/helpers/platformHelpers';
import ConnectedControllerCard from 'src/components/connected-controller/ConnectedControllerCard';
import { HomeMessages } from 'src/intl/HomeMessages';
import { bleActions } from 'src/redux/ble/actions';
import { useToast } from 'src/components/reusable-components/useToast';

export interface FilterOptionState {
  firmware?: string;
  organization?: string;
  startDate?: string;
  endDate?: string;
}

export const FILTER_PROPERTY = {
  ORGANIZATION: 'organization',
  FIRMWARE: 'firmware',
};

export const MobileControllersListPage: React.FC = () => {
  const intl = useIntl();
  const [present] = useToast();
  const { state: locationState } = useLocation<ControllerListPageState>();

  const { bleState, serialNumber, isLoading } = useBle();
  const userData = useSelector(selectAuthUserInfo);
  const dispatch = useDispatch();
  const history = useHistory();
  const isBleLocked = useSelector(selectBleIsLocked);
  const { isLimited } = useLimitedMode();
  const hasBluetooth = useHasBluetoothLE();

  const [fwSearchRequest] = useState<SearchFirmwareRequest>({
    hidden: 'false',
    page: 0,
    pageSize: 10000,
    sortOrder: 'desc',
    sortProperty: 'createdAt',
  });
  const { items: firmwareSearchResult, fetched: firmwareFetched } = useSocketSearch(
    SocketContextSentinel,
    'firmware',
    fwSearchRequest,
  );

  const [orgRequest] = useState<ListOrganizationsRequest>({
    statuses: ['active'],
    pageNumber: 0,
    pageSize: 1000,
    sort: { order: 'asc', property: 'orgName' },
  });
  const { items: organizations } = useSocketList(SocketContextPortal, 'organizations', orgRequest, 'listV2');

  const [filterOptionState, setFilterOptionState] = useState<FilterOptionState>({
    organization: userData?.org,
  });

  // Show deleted successful toast if the action occurred.
  React.useEffect(() => {
    if (locationState?.action === ControllerListPageActions.ReassignmentSuccessful) {
      history.replace({ state: {} });
      present(
        {
          message: intl.formatMessage(ControllerMessages.reassignmentSuccessful, {
            physicalSerial: locationState.controllerPhysicalSerial,
            organization: locationState.organizationName,
          }),
        },
        'success',
      );
      return;
    }
    if (locationState?.action === ControllerListPageActions.ReassignmentUnsuccessful) {
      history.replace({ state: {} });
      present(
        {
          message: intl.formatMessage(ControllerMessages.reassignmentUnsuccessful, {
            physicalSerial: locationState.controllerPhysicalSerial,
          }),
        },
        'danger',
      );
    }
  }, [history, locationState, present, intl]);

  const [updateFirmwareModalOpen, setUpdateFirmwareModelOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);

  // Users should only see the controller they are currently connected to unless they have permission
  const canViewNonConnectedControllers = useHasPermission('ViewNonConnectedControllers');
  const buildApiQuery = useMemo(() => {
    const orQuery = [
      { description: { $regex: escapeRegExp(searchText), $options: 'i' } },
      { physicalSerial: { $regex: escapeRegExp(searchText), $options: 'i' } },
    ];

    if (!!serialNumber) {
      (orQuery as any).push({ ssn: serialNumber }); // Allows the connected controller info to return.
    }

    const orgFilter = filterOptionState.organization
      ? { org: filterOptionState.organization }
      : Roles.isStoneAge(userData?.role)
        ? {}
        : { org: userData?.org };

    return {
      query: {
        $or: orQuery,
        $and: [
          filterOptionState.firmware ? { fw: filterOptionState.firmware } : {},
          orgFilter,
          canViewNonConnectedControllers ? {} : { physicalSerial: serialNumber ?? 'InvalidSerialNumber' },
        ],
      },
    };
  }, [
    searchText,
    serialNumber,
    filterOptionState.organization,
    filterOptionState.firmware,
    userData?.role,
    userData?.org,
    canViewNonConnectedControllers,
  ]);

  const applyFilter = useCallback((filterObject: FilterOptionState) => {
    setFilterModalOpen(false);
    setFilterOptionState(filterObject);
  }, []);

  const { items: controllersRaw }: { items: Controller[] } = useSocketList(
    SocketContextSentinel,
    'controllers',
    buildApiQuery,
  );

  const controllers = React.useMemo(() => {
    return bleState === BleConnectionState.CONNECTED
      ? controllersRaw.filter(controller => controller.ssn !== serialNumber)
      : controllersRaw;
  }, [bleState, controllersRaw, serialNumber]);

  const searchPlaceholder = intl.formatMessage(GenericMessages.searchPlaceholder);

  useIonViewWillEnter(() => {
    dispatch(appBarActions.setTitle('Controllers'));
  }, [dispatch]);

  const formattedValues = useCallback(
    (filterKey, filterValue) => {
      if (filterKey === FILTER_PROPERTY.FIRMWARE) {
        const fw: Firmware | undefined = firmwareSearchResult?.results?.find(firmware => firmware._id === filterValue);

        return getFirmwareDisplayFromFirmware(intl, fw);
      }
      return getOrgName(filterValue, organizations);
    },
    [organizations, firmwareSearchResult?.results, intl],
  );
  const updateFirmwareVersion = React.useCallback(
    async (firmware: Firmware | undefined) => {
      if (!isLoading) {
        setFilterModalOpen(false);
      }

      if (BleService.controller?.updateFirmware && firmware) {
        try {
          await BleService.controller.updateFirmware(firmware);
          setUpdateFirmwareModelOpen(false);
        } catch (err) {
          console.error('Error updating firmware');
        }
      } else {
        console.error('Could not update firmware!', firmware);
      }
    },
    [isLoading],
  );

  return (
    <IonPage>
      <MobileHeader />
      <IonContent scrollY={false} scrollX={false}>
        <div className="controller-content">
          <div className="controller-scroll-fixed page-content">
            {!isLimited && (
              <div className="searchbar">
                <IonSearchbar
                  placeholder={searchPlaceholder}
                  value={searchText}
                  onIonChange={e => setSearchText(e.detail.value!)}
                  debounce={1500}
                />
                <IonButton className="filter-icon" fill="clear" onClick={() => setFilterModalOpen(true)} color="dark">
                  <IonIcon icon={filter} />
                </IonButton>
              </div>
            )}

            {!!Object.entries(filterOptionState)?.length && (
              <div className={isLimited ? 'ion-padding-top' : ''}>
                {Object.entries(filterOptionState)
                  .filter(([_, filterValue]) => !!filterValue)
                  .map(([filterKey, filterValue]) => {
                    return (
                      <IonChip key={filterKey}>
                        {formattedValues(filterKey, filterValue)}
                        {!isLimited && (
                          <IonIcon
                            color="dark"
                            onClick={() => setFilterOptionState({ ...filterOptionState, [filterKey]: null })}
                            icon={close}
                          />
                        )}
                      </IonChip>
                    );
                  })}
              </div>
            )}
            {isLoading && !isBleLocked ? (
              <IonLoading isOpen={isLoading} spinner="circles" />
            ) : bleState === BleConnectionState.CONNECTED ? (
              <div className="ion-margin-vertical">
                <ConnectedControllerCard />
              </div>
            ) : (
              hasBluetooth && (
                <IonButton
                  className="ion-margin-top"
                  expand="block"
                  color="primary"
                  fill="solid"
                  onClick={() => dispatch(bleActions.openConnectControllerModal())}
                >
                  <IonLabel>{intl.formatMessage(HomeMessages.connectToController)}</IonLabel>
                </IonButton>
              )
            )}
          </div>
          {firmwareFetched && (
            <div className="controller-scroll-container ion-padding-top">
              {searchText && !controllers.length ? (
                <div className="search-message">
                  <DqText variant="h3">
                    <FormattedMessage {...ControllerMessages.noControllersMatchSearch} />
                  </DqText>
                </div>
              ) : (
                <MobileControllersGrid
                  firmwares={firmwareSearchResult?.results ?? []}
                  controllers={controllers}
                  totalCount={controllers.length}
                  organizations={organizations}
                />
              )}
            </div>
          )}
        </div>
        <FilterControllerModal
          organizations={organizations}
          firmwares={firmwareSearchResult?.results ?? []}
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          onSave={applyFilter}
        />
        <UpdateFirmwareModal
          firmwares={firmwareSearchResult?.results ?? []}
          isOpen={updateFirmwareModalOpen}
          onClose={() => setUpdateFirmwareModelOpen(false)}
          onSave={async (fw: Firmware | undefined) => await updateFirmwareVersion(fw)}
        />
      </IonContent>
    </IonPage>
  );
};
